import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Building2, Receipt, Wallet, Hash, Loader2, Save, Lock, ScrollText, ListPlus, DatabaseBackup, Download, Upload, AlertTriangle, Percent, CalendarClock } from 'lucide-react';
import { getOrganisationSettings, updateOrganisationSettings, exportAllData, restoreAllData } from '../lib/api';
import { useAuth } from '../context/AuthContext';

// One-tap starter set of rules (the organisation's official Rules & Regulations). Editable after loading.
const STARTER_RULES = [
  'Monthly advance payment would be taken on 1st day of every month. If any occupant desires to vacate, the 30-days prior notice is must and, in that case, if any deposit is there, the same would be refunded to him.',
  'Rent charges may vary and can be hiked upto 10% - 20% at any time by the organization without any prior notice.',
  'In case of urgent shifting or cancellation, if the occupant leaves after the 1st day of their rental month, they will be charged with an amount of 30-days rent, and in case the occupant leaves after the 15th day of the month, the occupant will be charged for the whole month.',
  'Kindly note that the 30-day notice is mandatory before vacating the premises. Otherwise, the entire deposit will be forfeited in any circumstances such as hospitalization, family emergencies, job relocation, etc.',
  'Occupants are required to keep all the articles of the room/premises in intact condition. In case of damage to article/s he would be liable to pay the replacement value/repair charges for the same.',
  'Occupants are advised to keep silence in the room/premises, so that the study of other occupants is not affected.',
  'Occupants are advised to keep safe their cash/articles/valuables themselves. Management does not carry any responsibility for the same. However, if any occupant desires to keep their valuable/cash with the Management, the locker facility (without any additional charge) is available.',
  'Occupants are advised to use minimum water/electricity though the electric bills for AC and Geyser have to be paid by the Occupants on sharing basis. Unnecessary use of water/electricity is prohibited.',
  'Occupants will be given a complimentary allowance of electricity bill of Rs.100/- per month. Any amount above the respective rates will have to be incurred by the occupants.',
  'Bed sheet, pillow and pillow covers will be arranged and washed by us. Occupant has to hand it over at office during vacating the place with keys of wardrobe and room.',
  'Any mishappening with the occupants would be the sole responsibility of himself/parents/guardian.',
  'All the occupants are cautioned not to indulge in unnecessary gossips in the premises as well as outside the premises. This affects the image of the occupants and premises as well.',
  'The occupants are not permitted to keep guest in their rooms to stay overnight and the occupants are also not allowed to take any outsiders to their room.',
  'Alcohol and drugs are strictly prohibited in the room/premises. If any occupant is caught with alcohol and drugs, the entire deposit will be forfeited and required action will be taken by admin staff.',
  'All occupants are required to maintain good conduct. Indiscipline state of the occupant will not be tolerated.',
  'No occupants shall share their room with anyone or allow using their room to someone in their absence.',
  'All electrical appliances will have to be switched off by the residents positively, when not in use.',
  'The cost of agreement will be payable by occupants and the process of police verification will be done by occupants.',
  'Management staff is permitted to access your room with or without your presence, for the purpose of checking and maintenance.',
  'Rent along with electricity is to be deposited within 10 days from when it is due. In case of late payment, Rs. 100/- Per day will be charged as Penalty for late payment. If rent is not received within 20 days of it becoming due, then the occupant will be asked to vacate the premises after payment of rent due.',
].join('\n');


// Indian GST State Codes (matches backend GST_STATE_CODES)
const GST_STATES = [
  ["01","Jammu & Kashmir"],["02","Himachal Pradesh"],["03","Punjab"],["04","Chandigarh"],
  ["05","Uttarakhand"],["06","Haryana"],["07","Delhi"],["08","Rajasthan"],["09","Uttar Pradesh"],
  ["10","Bihar"],["11","Sikkim"],["12","Arunachal Pradesh"],["13","Nagaland"],["14","Manipur"],
  ["15","Mizoram"],["16","Tripura"],["17","Meghalaya"],["18","Assam"],["19","West Bengal"],
  ["20","Jharkhand"],["21","Odisha"],["22","Chhattisgarh"],["23","Madhya Pradesh"],
  ["24","Gujarat"],["25","Daman & Diu"],["26","Dadra & Nagar Haveli"],["27","Maharashtra"],
  ["28","Andhra Pradesh"],["29","Karnataka"],["30","Goa"],["31","Lakshadweep"],
  ["32","Kerala"],["33","Tamil Nadu"],["34","Puducherry"],["35","Andaman & Nicobar"],
  ["36","Telangana"],["37","Andhra Pradesh (New)"],["38","Ladakh"]
];

export default function OrganisationSettings() {
  const { user } = useAuth();
  const isAdminUser = user?.role === 'admin' || user?.role === 'super_admin';
  const restoreInputRef = useRef(null);
  const [restoring, setRestoring] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);
  const [form, setForm] = useState({
    guest_id_prefix: '',
    business_name: '',
    business_address: '',
    business_mobile: '',
    business_email: '',
    gst_enabled: false,
    gstin: '',
    gst_rate: 18,
    gst_hsn: '9963',
    gst_threshold_amount: 0,
    gst_place_of_supply: '',
    logo_url: '',
    hostel_rules: '',
    late_penalty_rate: 100,
    penalty_grace_days: 10,
    rent_due_timing: 'month_start',
    opening_cash_balance: 0,
    opening_bank_balance: 0,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await getOrganisationSettings();
      setForm({
        guest_id_prefix: data.guest_id_prefix || '',
        business_name: data.business_name || '',
        business_address: data.business_address || '',
        business_mobile: data.business_mobile || '',
        business_email: data.business_email || '',
        gst_enabled: !!data.gst_enabled,
        gstin: data.gstin || '',
        gst_rate: data.gst_rate ?? 18,
        gst_hsn: data.gst_hsn || '9963',
        gst_threshold_amount: data.gst_threshold_amount ?? 0,
        gst_place_of_supply: data.gst_place_of_supply || '',
        logo_url: data.logo_url || '',
        hostel_rules: data.hostel_rules || '',
        late_penalty_rate: data.late_penalty_rate ?? 100,
        penalty_grace_days: data.penalty_grace_days ?? 10,
        rent_due_timing: data.rent_due_timing || 'month_start',
        opening_cash_balance: data.opening_cash_balance ?? 0,
        opening_bank_balance: data.opening_bank_balance ?? 0,
      });
      setLocked(!!data.guest_id_prefix_locked);
    } catch (e) {
      toast.error('Failed to load organisation settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!locked && !(form.guest_id_prefix || '').trim()) {
      toast.error('Guest ID Prefix is required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.late_penalty_rate === '' || payload.late_penalty_rate == null || isNaN(payload.late_penalty_rate)) payload.late_penalty_rate = 0;
      if (payload.penalty_grace_days === '' || payload.penalty_grace_days == null || isNaN(payload.penalty_grace_days)) payload.penalty_grace_days = 0;
      if (locked) delete payload.guest_id_prefix; // never resend a locked prefix
      const { data } = await updateOrganisationSettings(payload);
      setLocked(!!data.guest_id_prefix_locked);
      toast.success('Organisation settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportAllData();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `staypoint_backup_${ts}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleRestoreClick = () => restoreInputRef.current?.click();
  const handleRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting same file later
    if (!file) return;
    if (!window.confirm('Restore from this backup?\n\nThis will REPLACE your current data (guests, payments, records) with the contents of this file. This cannot be undone. Continue?')) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await restoreAllData(fd);
      toast.success(data.message || 'Backup restored successfully');
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore backup');
    } finally {
      setRestoring(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="org-settings-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-24" data-testid="organisation-settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Organisation Settings</h1>
          <p className="text-slate-500 mt-1">Applies to your whole organisation — all properties under this login</p>
        </div>
      </div>

      {/* Guest ID Prefix */}
      <Card className="relative overflow-hidden border-slate-200 shadow-sm hover:shadow-md transition-all duration-200">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-violet-600" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600"><Hash className="h-4 w-4" /></span>
            Guest ID Prefix
          </CardTitle>
          <CardDescription>
            Prefix for every Guest ID in this organisation, e.g. <b>{(form.guest_id_prefix || 'STP')}-0001</b>. One continuous sequence across all properties.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-w-xs">
            <Label htmlFor="prefix">Prefix <span className="text-rose-500">*</span></Label>
            <Input
              id="prefix"
              maxLength={6}
              value={form.guest_id_prefix}
              disabled={locked}
              className={locked ? 'bg-slate-100 cursor-not-allowed uppercase' : 'uppercase'}
              onChange={(e) => set('guest_id_prefix', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              placeholder="e.g., STP"
              data-testid="org-prefix-input"
            />
            {locked ? (
              <p className="text-xs text-amber-600 flex items-center gap-1"><Lock className="h-3 w-3" /> One-time setting — cannot be changed once saved.</p>
            ) : (
              <p className="text-xs text-slate-500"><b>Set once — it cannot be edited later.</b> Existing Guest IDs will be re-labelled with this prefix.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Business Details */}
      <Card className="relative overflow-hidden border-slate-200 shadow-sm hover:shadow-md transition-all duration-200">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-600" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600"><Building2 className="h-4 w-4" /></span>
            Business Details
          </CardTitle>
          <CardDescription>These details appear on all invoices and receipts.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="bn">Company/Business Name</Label>
            <Input id="bn" value={form.business_name} onChange={(e) => set('business_name', e.target.value.toUpperCase())} placeholder="e.g., STAY POINT PRIVATE LIMITED" data-testid="org-business-name-input" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="ba">Business Address</Label>
            <Input id="ba" value={form.business_address} onChange={(e) => set('business_address', e.target.value.toUpperCase())} placeholder="Full business address for invoice" data-testid="org-business-address-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="bm">Mobile/Phone</Label>
            <Input id="bm" value={form.business_mobile} onChange={(e) => set('business_mobile', e.target.value)} placeholder="9876543210" data-testid="org-business-mobile-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="be">Email</Label>
            <Input id="be" type="email" value={form.business_email} onChange={(e) => set('business_email', e.target.value.toLowerCase())} placeholder="info@example.com" style={{ textTransform: 'lowercase' }} data-testid="org-business-email-input" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="logo">Logo URL (Optional)</Label>
            <Input id="logo" value={form.logo_url} onChange={(e) => set('logo_url', e.target.value)} placeholder="https://example.com/logo.png" data-testid="org-logo-input" />
            <p className="text-xs text-slate-500">Enter URL to your company logo (PNG/JPG recommended)</p>
          </div>
        </CardContent>
      </Card>

      {/* GST */}
      <Card className="relative overflow-hidden border-slate-200 shadow-sm hover:shadow-md transition-all duration-200">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-600" />
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><Receipt className="h-4 w-4" /></span>
                GST Details
              </CardTitle>
              <CardDescription className="mt-1">Enable to add GSTIN and apply GST on invoices.</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium ${form.gst_enabled ? 'text-emerald-600' : 'text-slate-400'}`}>{form.gst_enabled ? 'Enabled' : 'Disabled'}</span>
              <Switch checked={form.gst_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, gst_enabled: v, ...(v ? {} : { gstin: '' }) }))} data-testid="org-gst-toggle" />
            </div>
          </div>
        </CardHeader>
        {form.gst_enabled && (
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="gstin">GSTIN</Label>
                <Input id="gstin" value={form.gstin} onChange={(e) => set('gstin', e.target.value.toUpperCase())} placeholder="22AAAAA0000A1Z5" maxLength={15} data-testid="org-gstin-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gst_rate">GST Rate (%)</Label>
                <Input id="gst_rate" type="number" min="0" max="28" step="0.5" value={form.gst_rate} onChange={(e) => set('gst_rate', parseFloat(e.target.value) || 18)} data-testid="org-gst-rate-input" />
                <p className="text-xs text-slate-500">Standard rate: 18%</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="gst_state_code">State Code</Label>
                <Input id="gst_state_code" value={form.gstin ? form.gstin.substring(0, 2) : ''} readOnly disabled placeholder="--" className="bg-slate-100 text-center font-mono" data-testid="org-gst-state-code" />
                <p className="text-xs text-slate-400">Auto from GSTIN (first 2 digits)</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="gst_hsn">HSN/SAC Code</Label>
                <Input id="gst_hsn" value={form.gst_hsn} onChange={(e) => set('gst_hsn', e.target.value)} placeholder="9963" maxLength={8} data-testid="org-gst-hsn-input" />
                <p className="text-xs text-slate-500">Shown on tax invoices</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="gst_threshold_amount">Threshold Amount (₹)</Label>
                <Input id="gst_threshold_amount" type="number" min="0" step="1" value={form.gst_threshold_amount} onChange={(e) => set('gst_threshold_amount', parseFloat(e.target.value) || 0)} placeholder="0" data-testid="org-gst-threshold-input" />
                <p className="text-xs text-slate-500">GST only when transaction &gt; this</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="gst_place_of_supply">Place of Supply</Label>
                <select
                  id="gst_place_of_supply"
                  value={form.gst_place_of_supply}
                  onChange={(e) => set('gst_place_of_supply', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  data-testid="org-gst-place-of-supply-select"
                >
                  <option value="">Same as State (GSTIN)</option>
                  {GST_STATES.map(([code, nm]) => (
                    <option key={code} value={code}>{code} - {nm}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500">State of supply (on invoice)</p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Opening Balances */}
      <Card className="relative overflow-hidden border-slate-200 shadow-sm hover:shadow-md transition-all duration-200">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-orange-600" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-600"><Wallet className="h-4 w-4" /></span>
            Financial Opening Balances
          </CardTitle>
          <CardDescription>Actual cash &amp; bank balance as on your System Start Date. Used as the opening balance in Daily Hisab.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="cash">Opening Cash Balance (₹)</Label>
            <Input id="cash" type="number" value={form.opening_cash_balance} onChange={(e) => set('opening_cash_balance', parseFloat(e.target.value) || 0)} data-testid="org-opening-cash-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="bank">Opening Bank Balance (₹)</Label>
            <Input id="bank" type="number" value={form.opening_bank_balance} onChange={(e) => set('opening_bank_balance', parseFloat(e.target.value) || 0)} data-testid="org-opening-bank-input" />
          </div>
        </CardContent>
      </Card>

      {/* Late Payment Penalty */}
      <Card className="relative overflow-hidden border-slate-200 shadow-sm hover:shadow-md transition-all duration-200">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-500 to-red-600" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 text-rose-600"><Percent className="h-4 w-4" /></span>
            Late Payment Penalty
          </CardTitle>
          <CardDescription>
            Used to suggest late-payment penalties on overdue rent. Penalty starts <b>after</b> the grace period from the rent due date and is charged per day at the rate below. You can always edit the suggested amount before saving a penalty.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="penalty_rate">Late Penalty Rate (₹ / day)</Label>
            <Input id="penalty_rate" type="number" min="0" step="1" value={form.late_penalty_rate} onChange={(e) => set('late_penalty_rate', e.target.value === '' ? '' : parseFloat(e.target.value))} data-testid="org-penalty-rate-input" />
            <p className="text-xs text-slate-500">Amount charged per day of delay.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="grace_days">Grace Period (days)</Label>
            <Input id="grace_days" type="number" min="0" max="60" step="1" value={form.penalty_grace_days} onChange={(e) => set('penalty_grace_days', e.target.value === '' ? '' : parseInt(e.target.value, 10))} data-testid="org-penalty-grace-input" />
            <p className="text-xs text-slate-500">
              No penalty during these days after the due date. Penalty starts the very next day.
              {Number.isFinite(Number(form.penalty_grace_days)) && (
                <> E.g., grace of <b>{Number(form.penalty_grace_days)}</b> day(s) → penalty begins on day <b>{Number(form.penalty_grace_days) + 1}</b> after the due date.</>
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Rent Due Timing */}
      <Card className="relative overflow-hidden border-slate-200 shadow-sm hover:shadow-md transition-all duration-200" data-testid="rent-due-timing-card">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-sky-500 to-blue-600" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-sky-50 text-sky-600"><CalendarClock className="h-4 w-4" /></span>
            Rent Due Timing
          </CardTitle>
          <CardDescription>
            Controls when the <b>current month's</b> rent starts showing as due across the Guests tab, Outstanding Dues, Final Settlement (Ledger) and the Dashboard. Past unpaid months always show regardless of this setting.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { val: 'month_start', title: "Current month's rent due should show from the 1st", desc: 'Default. The whole current month is treated as due from the 1st, even before each guest\u2019s due date arrives.' },
            { val: 'on_due_date', title: "Current month's rent due should show only on each guest's due date", desc: 'The current month\u2019s rent appears as due only once that guest\u2019s rent due day arrives (e.g. a 21st-cycle guest shows due on the 21st, not the 1st).' },
          ].map((opt) => {
            const active = form.rent_due_timing === opt.val;
            return (
              <button
                type="button"
                key={opt.val}
                onClick={() => set('rent_due_timing', opt.val)}
                className={`w-full text-left flex items-start gap-3 rounded-xl border p-4 transition-all ${active ? 'border-sky-500 bg-sky-50/60 ring-1 ring-sky-400' : 'border-slate-200 hover:bg-slate-50'}`}
                data-testid={`rent-due-timing-${opt.val}`}
              >
                <span className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${active ? 'border-sky-600' : 'border-slate-300'}`}>
                  {active && <span className="h-2 w-2 rounded-full bg-sky-600" />}
                </span>
                <span className="min-w-0">
                  <span className={`block text-sm font-semibold ${active ? 'text-sky-800' : 'text-slate-800'}`}>{opt.title}</span>
                  <span className="block text-xs text-slate-500 mt-0.5">{opt.desc}</span>
                </span>
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* Hostel / PG Rules */}
      <Card className="relative overflow-hidden border-slate-200 shadow-sm hover:shadow-md transition-all duration-200">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-fuchsia-500 to-pink-600" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-fuchsia-50 text-fuchsia-600"><ScrollText className="h-4 w-4" /></span>
            Hostel / PG Rules
          </CardTitle>
          <CardDescription>
            One rule per line. These are auto-numbered (1, 2, 3…) and printed on the back page of the Guest Registration Form PDF, above the guest's signature.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="rules">Rules &amp; Regulations</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => {
                  if (form.hostel_rules.trim() && !window.confirm('Replace the current rules with the standard starter set? Your existing text will be overwritten.')) return;
                  set('hostel_rules', STARTER_RULES);
                }}
                data-testid="load-starter-rules-btn"
              >
                <ListPlus className="h-3.5 w-3.5 mr-1" /> Load starter rules
              </Button>
            </div>
            <textarea
              id="rules"
              value={form.hostel_rules}
              onChange={(e) => set('hostel_rules', e.target.value)}
              rows={8}
              placeholder={"Rent is due on or before the 5th of every month.\nVisitors are not allowed after 9 PM.\nDamage to property will be charged from the deposit.\nOne month notice is required before leaving."}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 leading-relaxed"
              data-testid="org-rules-input"
            />
            <p className="text-xs text-slate-500">{(form.hostel_rules || '').split('\n').filter((l) => l.trim()).length} rule(s) — each line becomes a numbered point on the PDF. Edit freely after loading the starter set.</p>
          </div>
        </CardContent>
      </Card>

      {/* Data Backup & Restore — admin only */}
      {isAdminUser && (
      <Card className="relative overflow-hidden border-slate-200 shadow-sm hover:shadow-md transition-all duration-200">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-600" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><DatabaseBackup className="h-4 w-4" /></span>
            Data Backup &amp; Restore
          </CardTitle>
          <CardDescription>
            Download a complete backup of your organisation's data (guests, payments, ledgers, properties, receipts) as a JSON file, and restore it later if needed. Available to administrators only. We recommend downloading a backup periodically and keeping it safely off-site.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Button type="button" variant="outline" onClick={handleExport} disabled={exporting} data-testid="export-data-btn">
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Download Full Backup (JSON)
            </Button>
            <p className="text-xs text-slate-400 mt-2">Contains all records for your organisation as of the moment you download it.</p>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800">
                <strong>Restore</strong> replaces your current data with the contents of a backup file. This cannot be undone — download a fresh backup first if unsure.
              </p>
            </div>
            <input ref={restoreInputRef} type="file" accept="application/json,.json" onChange={handleRestoreFile} className="hidden" data-testid="restore-file-input" />
            <Button type="button" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100" onClick={handleRestoreClick} disabled={restoring} data-testid="restore-data-btn">
              {restoring ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Restore from Backup (JSON)
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      <div className="flex items-center justify-end gap-3 sticky bottom-0 -mx-1 px-1 py-3 bg-gradient-to-t from-white via-white/95 to-white/0">
        <Button onClick={handleSave} disabled={saving} size="lg" className="shadow-sm transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0" data-testid="org-settings-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Settings
        </Button>
      </div>
    </div>
  );
}
